<template>
  <div class="flex flex-col gap-2" data-testid="assembly-mate-property-section">
    <n-collapse
      :expanded-names="expandedNames"
      @update:expanded-names="handleExpandedNamesChange"
    >
      <PuppyCollapseItem title="配合选择" name="entities">
        <PuppyInputSelect
          :items="entityItems"
          :active="entityInputActive"
          :active-index="entityActiveIndex"
          :selected-indices="entitySelectedIndices"
          :visible-rows="2"
          item-key-field="id"
          item-label-field="label"
          empty-text="请选择两个可配合实体"
          delete-label="删除选择"
          delete-all-label="删除所有选择"
          :show-delete-all-action="entityItems.length > 1"
          selection-behavior="ctrl-multiple"
          @update:active="handleEntityInputActiveUpdate"
          @update:active-index="handleEntityActiveIndexUpdate"
          @delete-item="handleDeleteEntityItem"
          @delete-all="handleDeleteAllEntityItems"
        >
          <template #item="{ item }">
            <div class="min-w-0 flex flex-col">
              <span class="truncate text-xs">{{ (item as MateEntitySelectItem).label }}</span>
              <span class="truncate text-[10px] text-[var(--n-text-color-3)]">
                {{ (item as MateEntitySelectItem).subtitle }}
              </span>
            </div>
          </template>
        </PuppyInputSelect>

        <div
          v-if="state.selectedMateType === AssemblyMateType.Angle"
          class="mt-2"
        >
          <div class="mb-1 text-xs text-[var(--n-text-color-2)]">
            参考实体
          </div>
          <PuppyInputSelect
            :items="angleReferenceItems"
            :active="angleReferenceInputActive"
            :active-index="angleReferenceActiveIndex"
            :selected-indices="angleReferenceSelectedIndices"
            :visible-rows="1"
            item-key-field="id"
            item-label-field="label"
            empty-text="自动参考"
            delete-label="清除参考实体"
            :show-delete-all-action="false"
            @update:active="handleAngleReferenceInputActiveUpdate"
            @update:active-index="handleAngleReferenceActiveIndexUpdate"
            @delete-item="handleDeleteAngleReferenceItem"
          >
            <template #item="{ item }">
              <div class="min-w-0 flex flex-col">
                <span class="truncate text-xs">{{ (item as MateEntitySelectItem).label }}</span>
                <span class="truncate text-[10px] text-[var(--n-text-color-3)]">
                  {{ (item as MateEntitySelectItem).subtitle }}
                </span>
              </div>
            </template>
          </PuppyInputSelect>
        </div>
      </PuppyCollapseItem>

      <PuppyCollapseItem title="配合类型" name="types">
        <div class="flex flex-col gap-1">
          <n-tooltip
            v-for="item in primaryMateTypeRows"
            :key="item.mateType"
            :disabled="isMateTypeEnabled(item.mateType) || !resolveMateTypeDisabledReason(item.mateType)"
          >
            <template #trigger>
              <span class="inline-flex w-full">
                <n-button
                  block
                  size="small"
                  class="justify-start"
                  :type="state.selectedMateType === item.mateType ? 'primary' : 'default'"
                  :disabled="!isMateTypeEnabled(item.mateType)"
                  @click="handleMateTypeClick(item.mateType)"
                >
                  <template #icon>
                    <MateTypeIcon :mate-type="item.mateType" />
                  </template>
                  {{ item.label }}
                </n-button>
              </span>
            </template>
            {{ resolveMateTypeDisabledReason(item.mateType) }}
          </n-tooltip>
        </div>

        <div class="mt-3 flex flex-col gap-2">
          <div class="flex items-center gap-2">
            <n-tooltip
              :disabled="isMateTypeEnabled(AssemblyMateType.Distance) || !resolveMateTypeDisabledReason(AssemblyMateType.Distance)"
            >
              <template #trigger>
                <span class="inline-flex">
                  <n-button
                    size="small"
                    :type="state.selectedMateType === AssemblyMateType.Distance ? 'primary' : 'default'"
                    :disabled="state.selectedMateType !== AssemblyMateType.Distance && !isMateTypeEnabled(AssemblyMateType.Distance)"
                    @click="handleMateTypeClick(AssemblyMateType.Distance)"
                  >
                    <template #icon>
                      <MateTypeIcon :mate-type="AssemblyMateType.Distance" />
                    </template>
                  </n-button>
                </span>
              </template>
              {{ resolveMateTypeDisabledReason(AssemblyMateType.Distance) }}
            </n-tooltip>
            <n-input-number
              class="min-w-0 flex-1"
              :value="distanceDraftValue"
              size="small"
              :precision="3"
              :step="1"
              :disabled="state.selectedMateType !== AssemblyMateType.Distance"
              data-testid="assembly-mate-distance-input"
              @update:value="handleDistanceChange"
            />
          </div>

          <n-checkbox
            v-if="state.selectedMateType === AssemblyMateType.Distance"
            :checked="state.dimensionFlipped"
            data-testid="assembly-mate-distance-flip-dimension"
            @update:checked="handleDimensionFlippedChange"
          >
            反转尺寸
          </n-checkbox>

          <div
            v-if="state.selectedMateType === AssemblyMateType.Distance && hasRoundDistanceModeRows"
            class="pl-8 flex flex-col gap-1"
          >
            <div
              v-if="firstDistanceModeItems.length > 0"
              class="flex flex-col gap-1"
            >
              <n-radio-group
                :value="state.distanceEntityModes.firstEntityMode"
                size="small"
                @update:value="(value) => handleDistanceEntityModeValueChange(0, value)"
              >
                <n-radio-button
                  v-for="item in firstDistanceModeItems"
                  :key="item.mode"
                  :value="item.mode"
                  :disabled="!item.enabled"
                >
                  {{ distanceModeLabelMap[item.mode] }}
                </n-radio-button>
              </n-radio-group>
            </div>
            <div
              v-if="secondDistanceModeItems.length > 0"
              class="flex flex-col gap-1"
            >
              <n-radio-group
                :value="state.distanceEntityModes.secondEntityMode"
                size="small"
                @update:value="(value) => handleDistanceEntityModeValueChange(1, value)"
              >
                <n-radio-button
                  v-for="item in secondDistanceModeItems"
                  :key="item.mode"
                  :value="item.mode"
                  :disabled="!item.enabled"
                >
                  {{ distanceModeLabelMap[item.mode] }}
                </n-radio-button>
              </n-radio-group>
            </div>
          </div>

          <n-checkbox
            v-if="state.selectedMateType === AssemblyMateType.Concentric && state.lockRotationEnabled"
            :checked="state.lockRotation"
            @update:checked="handleLockRotationChange"
          >
            锁定旋转
          </n-checkbox>

          <div class="flex items-center gap-2">
            <n-tooltip
              :disabled="isMateTypeEnabled(AssemblyMateType.Angle) || !resolveMateTypeDisabledReason(AssemblyMateType.Angle)"
            >
              <template #trigger>
                <span class="inline-flex">
                  <n-button
                    size="small"
                    :type="state.selectedMateType === AssemblyMateType.Angle ? 'primary' : 'default'"
                    :disabled="state.selectedMateType !== AssemblyMateType.Angle && !isMateTypeEnabled(AssemblyMateType.Angle)"
                    @click="handleMateTypeClick(AssemblyMateType.Angle)"
                  >
                    <template #icon>
                      <MateTypeIcon :mate-type="AssemblyMateType.Angle" />
                    </template>
                  </n-button>
                </span>
              </template>
              {{ resolveMateTypeDisabledReason(AssemblyMateType.Angle) }}
            </n-tooltip>
            <n-input-number
              class="min-w-0 flex-1"
              :value="angleDegrees"
              size="small"
              :precision="2"
              :step="1"
              :disabled="state.selectedMateType !== AssemblyMateType.Angle"
              data-testid="assembly-mate-angle-input"
              @update:value="handleAngleDegreesChange"
            />
          </div>

          <n-checkbox
            v-if="state.selectedMateType === AssemblyMateType.Angle"
            :checked="state.dimensionFlipped"
            class="pl-8"
            data-testid="assembly-mate-angle-flip-dimension"
            @update:checked="handleDimensionFlippedChange"
          >
            反转尺寸
          </n-checkbox>

          <n-radio-group
            :value="state.alignment"
            size="small"
            class="pl-8"
            :disabled="!state.alignmentEnabled"
            @update:value="handleAlignmentValueChange"
          >
            <n-radio-button
              :value="MateAlignment.Aligned"
              :disabled="!isAlignmentBranchEnabled(MateAlignment.Aligned)"
            >
              同向对齐
            </n-radio-button>
            <n-radio-button
              :value="MateAlignment.AntiAligned"
              :disabled="!isAlignmentBranchEnabled(MateAlignment.AntiAligned)"
            >
              反向对齐
            </n-radio-button>
          </n-radio-group>
        </div>
      </PuppyCollapseItem>

      <PuppyCollapseItem title="配合" name="mates">
        <PuppyInputSelect
          :items="mateRelationItems"
          :active="mateInputActive"
          :active-index="mateActiveIndex"
          :selected-indices="mateSelectedIndices"
          :visible-rows="4"
          item-key-field="id"
          item-label-field="label"
          empty-text="尚未创建配合"
          delete-label="删除配合"
          :show-delete-all-action="false"
          selection-behavior="ctrl-multiple"
          @update:active="handleMateInputActiveUpdate"
          @update:active-index="handleMateActiveIndexUpdate"
          @update:selected-indices="handleMateSelectedIndicesUpdate"
          @delete-item="handleDeleteMateItem"
        >
          <template #item="{ item }">
            <div class="min-w-0 flex flex-col">
              <span class="truncate text-xs">{{ (item as MateRelationSelectItem).label }}</span>
              <span class="truncate text-[10px] text-[var(--n-text-color-3)]">
                {{ (item as MateRelationSelectItem).subtitle }}
              </span>
            </div>
          </template>
        </PuppyInputSelect>
      </PuppyCollapseItem>

      <PuppyCollapseItem title="选项" name="options">
        <div class="flex flex-col gap-2">
          <n-checkbox
            :checked="state.showPreview"
            @update:checked="handleShowPreviewChange"
          >
            显示预览
          </n-checkbox>
          <n-checkbox
            :checked="state.firstSelectionTransparent"
            data-testid="assembly-mate-first-selection-transparent"
            @update:checked="handleFirstSelectionTransparentChange"
          >
            使第一个选择透明
          </n-checkbox>
        </div>
      </PuppyCollapseItem>
    </n-collapse>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import {
  AssemblyMateType,
  MateAlignment,
  MateDistanceMode,
  createDefaultAssemblyMateControllerState,
  projectAssemblyMatesToTree,
  resolveAssemblyMateTypeDisplayLabel,
  type AssemblyMateControllerState,
  type AssemblyMateControllerViewModel,
  type AssemblyMateDefinition,
  type AssemblyMateTypeAvailability,
} from '@puppycad/cad-core';
import {
  NButton,
  NCheckbox,
  NCollapse,
  NInputNumber,
  NRadioButton,
  NRadioGroup,
  NTooltip,
} from 'naive-ui';
import MateTypeIcon from './MateTypeIcon.vue';
import PuppyCollapseItem from '@/components/PuppyCollapseItem/PuppyCollapseItem.vue';
import PuppyInputSelect from '@/components/PuppyInputSelect/PuppyInputSelect.vue';
import type { PropertyManagerContext } from '@/core';
import { resolveAssemblyMateController } from '@/utils';
import { requestFeatureTreeDeleteConfirmation } from '@/views/editor/requestFeatureTreeDeleteConfirmation';

interface AssemblyMatePropertySectionProps {
  /** 属性面板上下文。 */
  context: PropertyManagerContext;
}

interface MateEntitySelectItem {
  /** 列表项稳定 ID。 */
  readonly id: string;
  /** 主显示文案。 */
  readonly label: string;
  /** 次级显示文案。 */
  readonly subtitle: string;
}

interface MateRelationSelectItem {
  /** Mate ID。 */
  readonly id: string;
  /** 配合类型显示文案。 */
  readonly label: string;
  /** 两个实体之间的关系文案。 */
  readonly subtitle: string;
}

interface MateTypeRow {
  /** 配合类型。 */
  readonly mateType: AssemblyMateType;
  /** 配合类型名称。 */
  readonly label: string;
}

const props = defineProps<AssemblyMatePropertySectionProps>();
const expandedNames = ref(['entities', 'types', 'mates', 'options']);
const entityInputActive = ref(false);
const entityActiveIndex = ref(-1);
const angleReferenceInputActive = ref(false);
const angleReferenceActiveIndex = ref(-1);
const mateInputActive = ref(false);
const mateActiveIndex = ref(-1);
const mateSelectedIndices = ref<number[]>([]);

const distanceModeLabelMap: Record<MateDistanceMode, string> = {
  [MateDistanceMode.CenterToCenter]: '中心到中心',
  [MateDistanceMode.Minimum]: '最小距离',
  [MateDistanceMode.Maximum]: '最大距离',
};

const primaryMateTypeRows: readonly MateTypeRow[] = [
  AssemblyMateType.Coincident,
  AssemblyMateType.Parallel,
  AssemblyMateType.Perpendicular,
  AssemblyMateType.Tangent,
  AssemblyMateType.Concentric,
  AssemblyMateType.Lock,
].map((mateType) => ({
  mateType,
  label: resolveAssemblyMateTypeDisplayLabel(mateType),
}));

const defaultState: AssemblyMateControllerState = createDefaultAssemblyMateControllerState();

const state = ref<AssemblyMateControllerState>(defaultState);
const sessionMates = ref<readonly AssemblyMateDefinition[]>([]);
const mateDisplayLabelById = ref<ReadonlyMap<string, string>>(new Map());

/** 距离输入显示精度。 */
const DISTANCE_INPUT_PRECISION = 3;
/** 距离输入框草稿值，避免 Naive UI 编辑态被 cad-core 完整浮点数反复覆盖。 */
const distanceDraftValue = ref(roundNumericInputValue(defaultState.distance, DISTANCE_INPUT_PRECISION));

const angleDegrees = computed(() => Number((state.value.angle * 180 / Math.PI).toFixed(2)));
const firstDistanceModeItems = computed(() => state.value.firstDistanceModeOptions);
const secondDistanceModeItems = computed(() => state.value.secondDistanceModeOptions);
const hasRoundDistanceModeRows = computed(() => (
  firstDistanceModeItems.value.length > 0 || secondDistanceModeItems.value.length > 0
));
const entityItems = computed<MateEntitySelectItem[]>(() => state.value.candidates.slice(0, 2).map((candidate, index) => ({
  id: candidate.entityRef.stableRef,
  label: `${index + 1}. ${candidate.label}`,
  subtitle: candidate.entityRef.assemblyNodeId,
})));
const entitySelectedIndices = computed(() => entityItems.value.map((_, index) => index));
const angleReferenceItems = computed<MateEntitySelectItem[]>(() => {
  const referenceEntity = state.value.angleReferenceEntity;
  if (!referenceEntity) {
    return [];
  }
  return [{
    id: referenceEntity.stableRef,
    label: referenceEntity.label,
    subtitle: referenceEntity.assemblyNodeId,
  }];
});
const angleReferenceSelectedIndices = computed(() => angleReferenceItems.value.map((_, index) => index));
const mateRelationItems = computed<MateRelationSelectItem[]>(() => (
  sessionMates.value.map(formatMateRelationItem)
));

let unsubscribeState: (() => void) | null = null;

/**
 * 解析配合类型可用性。
 *
 * @param mateType 配合类型。
 * @returns 可用性结果。
 */
function resolveMateTypeAvailability(mateType: AssemblyMateType): AssemblyMateTypeAvailability {
  return state.value.compatibility?.mateTypes.find((item) => item.mateType === mateType)
    ?? {
      mateType,
      enabled: false,
      disabledReason: '请选择两个实体。',
    };
}

/**
 * 判断配合类型是否可用。
 *
 * @param mateType 配合类型。
 * @returns 是否可用。
 */
function isMateTypeEnabled(mateType: AssemblyMateType): boolean {
  return resolveMateTypeAvailability(mateType).enabled;
}

/**
 * 解析配合类型禁用原因。
 *
 * @param mateType 配合类型。
 * @returns 禁用原因。
 */
function resolveMateTypeDisabledReason(mateType: AssemblyMateType): string | undefined {
  return resolveMateTypeAvailability(mateType).disabledReason;
}

/**
 * 解析 cad-core 装配体配合控制器。
 *
 * @returns 配合控制器；未挂载时返回 null。
 */
function getAssemblyMateController() {
  return resolveAssemblyMateController(props.context.renderer);
}

/**
 * 将 cad-core 视图模型同步到 UI。
 *
 * @param viewModel cad-core 控制器视图模型。
 */
function applyViewModel(viewModel: AssemblyMateControllerViewModel): void {
  state.value = viewModel.state;
  sessionMates.value = viewModel.sessionMates;
  mateDisplayLabelById.value = new Map(
    (projectAssemblyMatesToTree(viewModel.document).children ?? [])
      .flatMap((node) => (node.mateId ? [[node.mateId, node.label] as const] : [])),
  );
  entityActiveIndex.value = viewModel.state.candidateReplacementIndex ?? -1;
  angleReferenceInputActive.value = viewModel.state.active
    && viewModel.state.selectionTarget === 'angleReference';
  entityInputActive.value = viewModel.state.active
    && viewModel.state.selectionTarget === 'mateEntities';
  distanceDraftValue.value = roundNumericInputValue(viewModel.state.distance, DISTANCE_INPUT_PRECISION);
}

/**
 * 主动读取 cad-core 控制器状态。
 */
function syncFromController(): void {
  const viewModel = getAssemblyMateController()?.getViewModel?.();
  if (!viewModel) {
    return;
  }
  applyViewModel(viewModel);
}

/**
 * 格式化已创建配合列表项。
 *
 * @param mate Mate Definition。
 * @returns PuppyInputSelect 可消费的列表项。
 */
function formatMateRelationItem(mate: AssemblyMateDefinition): MateRelationSelectItem {
  const label = mateDisplayLabelById.value.get(mate.mateId)
    ?? resolveAssemblyMateTypeDisplayLabel(mate.mateType);
  return {
    id: mate.mateId,
    label,
    subtitle: `${mate.firstEntity.label} ↔ ${mate.secondEntity.label}`,
  };
}

/**
 * 首次渲染前先同步 cad-core 状态。
 *
 * 设计说明：
 * PropertyManager 打开时不能先显示默认距离、默认诊断或空配合列表再等 mounted 订阅刷新；
 * SOLIDWORKS 的面板打开即显示当前 Mate 状态，因此这里在 setup 阶段主动读取一次控制器快照。
 */
syncFromController();

onMounted(() => {
  const controller = getAssemblyMateController();
  if (controller?.subscribeStateChanged) {
    unsubscribeState = controller.subscribeStateChanged(applyViewModel);
    return;
  }
  syncFromController();
});

onBeforeUnmount(() => {
  if (unsubscribeState) {
    unsubscribeState();
    unsubscribeState = null;
  }
});

/**
 * 同步展开的折叠项。
 *
 * @param names 当前展开项。
 */
function handleExpandedNamesChange(names: Array<string | number>): void {
  expandedNames.value = names.map((name) => String(name));
}

/**
 * 设置实体选择框激活态。
 *
 * @param active 是否激活。
 */
function handleEntityInputActiveUpdate(active: boolean): void {
  entityInputActive.value = active;
  if (!active) {
    getAssemblyMateController()?.setCandidateReplacementIndex?.(null);
    return;
  }
  angleReferenceInputActive.value = false;
  getAssemblyMateController()?.setSelectionTarget?.('mateEntities');
  syncFromController();
}

/**
 * 设置实体选择框当前行。
 *
 * @param index 当前行索引。
 */
function handleEntityActiveIndexUpdate(index: number): void {
  entityActiveIndex.value = index;
  getAssemblyMateController()?.setCandidateReplacementIndex?.(index >= 0 ? index : null);
  syncFromController();
}

/**
 * 删除配合选择中的单个实体。
 *
 * @param payload PuppyInputSelect 删除事件。
 */
function handleDeleteEntityItem(payload: { item: unknown; index: number }): void {
  getAssemblyMateController()?.removeCandidateAt?.(payload.index);
  syncFromController();
}

/**
 * 删除配合选择中的所有当前选中实体。
 */
function handleDeleteAllEntityItems(): void {
  const indices = entityItems.value.map((_, index) => index);
  getAssemblyMateController()?.removeCandidatesAt?.(indices);
  syncFromController();
}

/**
 * 设置 Angle Mate 参考实体输入框激活状态。
 *
 * 设计说明：
 * Reference Entity 是角度配合的第三输入槽，不能挤占 `配合选择` 中两个标准实体槽位；
 * 因此 UI 只把当前输入目标转发给 cad-core，由控制器决定后续 SelectionUpdated 的归属。
 *
 * @param active 是否激活。
 */
function handleAngleReferenceInputActiveUpdate(active: boolean): void {
  angleReferenceInputActive.value = active;
  const controller = getAssemblyMateController();
  if (active) {
    entityInputActive.value = false;
    controller?.setCandidateReplacementIndex?.(null);
    controller?.setSelectionTarget?.('angleReference');
    syncFromController();
    return;
  }
  if (state.value.selectionTarget === 'angleReference') {
    controller?.setSelectionTarget?.('mateEntities');
    syncFromController();
  }
}

/**
 * 设置 Angle Mate 参考实体输入框当前行。
 *
 * @param index 当前行索引。
 */
function handleAngleReferenceActiveIndexUpdate(index: number): void {
  angleReferenceActiveIndex.value = index;
}

/**
 * 清除 Angle Mate 手动参考实体。
 */
function handleDeleteAngleReferenceItem(): void {
  getAssemblyMateController()?.clearAngleReferenceEntity?.();
  syncFromController();
}

/**
 * 设置配合列表激活态。
 *
 * @param active 是否激活。
 */
function handleMateInputActiveUpdate(active: boolean): void {
  mateInputActive.value = active;
}

/**
 * 设置配合列表当前行。
 *
 * @param index 当前行索引。
 */
function handleMateActiveIndexUpdate(index: number): void {
  mateActiveIndex.value = index;
}

/**
 * 设置配合列表选中项。
 *
 * @param indices 选中索引。
 */
function handleMateSelectedIndicesUpdate(indices: number[]): void {
  mateSelectedIndices.value = indices;
  const selectedMateIds = indices
    .map((index) => mateRelationItems.value[index]?.id ?? null)
    .filter((mateId): mateId is string => typeof mateId === 'string' && mateId.length > 0);
  const controller = getAssemblyMateController();
  if (controller?.selectMates) {
    controller.selectMates(selectedMateIds);
    return;
  }
  controller?.selectMate?.(selectedMateIds[0] ?? null);
}

/**
 * 删除右键选中的配合。
 *
 * @param payload PuppyInputSelect 删除事件。
 */
async function handleDeleteMateItem(payload: { item: unknown; index: number }): Promise<void> {
  const item = payload.item as MateRelationSelectItem;
  if (!item.id) {
    return;
  }
  if (!props.context.dialog) {
    return;
  }
  if (!await requestFeatureTreeDeleteConfirmation({
    dialog: props.context.dialog,
    target: {
      id: item.id,
      label: item.label,
      kind: 'mate',
    },
    relatedItems: [],
  })) {
    return;
  }
  await getAssemblyMateController()?.deleteMate?.(item.id);
  getAssemblyMateController()?.selectMate?.(null);
  mateSelectedIndices.value = [];
  syncFromController();
}

/**
 * 设置当前配合类型。
 *
 * @param mateType 配合类型。
 */
function handleMateTypeClick(mateType: AssemblyMateType): void {
  getAssemblyMateController()?.setMateType?.(mateType);
  syncFromController();
}

/**
 * 设置配合对齐方向。
 *
 * @param value 对齐方向。
 */
function handleAlignmentChange(value: MateAlignment): void {
  getAssemblyMateController()?.setAlignment?.(value);
  syncFromController();
}

/**
 * 判断当前对齐分支是否可选。
 *
 * @param alignment 对齐分支。
 * @returns 是否可选。
 */
function isAlignmentBranchEnabled(alignment: MateAlignment): boolean {
  if (!state.value.alignmentEnabled) {
    return false;
  }
  return alignment === MateAlignment.Aligned
    ? state.value.alignmentBranchAvailability.aligned
    : state.value.alignmentBranchAvailability.antiAligned;
}

/**
 * 判断 Naive UI 单选组回传值是否为合法配合对齐方向。
 *
 * @param value 单选组回传值。
 * @returns 是否为 MateAlignment 枚举值。
 */
function isMateAlignment(value: unknown): value is MateAlignment {
  return Object.values(MateAlignment).includes(value as MateAlignment);
}

/**
 * 处理配合对齐方式单选组变化。
 *
 * @param value Naive UI 单选组回传值。
 */
function handleAlignmentValueChange(value: string | number | boolean): void {
  if (!isMateAlignment(value)) {
    return;
  }
  handleAlignmentChange(value);
}

/**
 * 设置距离值。
 *
 * @param value 输入值。
 */
function handleDistanceChange(value: number | null): void {
  if (value === null || !Number.isFinite(value)) {
    return;
  }
  distanceDraftValue.value = value;
  getAssemblyMateController()?.setDistance?.(value);
  syncFromController();
}

/**
 * 判断 Naive UI 单选组回传值是否为合法距离条件。
 *
 * @param value 单选组回传值。
 * @returns 是否为距离条件枚举。
 */
function isMateDistanceMode(value: unknown): value is MateDistanceMode {
  return Object.values(MateDistanceMode).includes(value as MateDistanceMode);
}

/**
 * 将浮点尺寸归一化为输入框显示值。
 *
 * 设计说明：cad-core 保留完整求解数值，UI 仅截断展示值，避免长浮点文本覆盖
 * Naive UI 输入框的步进控件后看起来像“中划线”的无效输入。
 *
 * @param value cad-core 输出的尺寸值。
 * @param precision 显示小数位。
 * @returns 输入框可稳定展示的数值。
 */
function roundNumericInputValue(value: number, precision: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }
  const factor = 10 ** precision;
  return Math.round(value * factor) / factor;
}

/**
 * 设置指定圆弧条件。
 *
 * @param entityIndex 实体索引。
 * @param distanceMode 距离测量条件。
 */
function handleDistanceEntityModeClick(entityIndex: 0 | 1, distanceMode: MateDistanceMode): void {
  getAssemblyMateController()?.setDistanceEntityMode?.(entityIndex, distanceMode);
  syncFromController();
}

/**
 * 处理逐实体圆弧条件单选组变化。
 *
 * @param entityIndex 实体索引。
 * @param value Naive UI 单选组回传值。
 */
function handleDistanceEntityModeValueChange(entityIndex: 0 | 1, value: string | number | boolean): void {
  if (!isMateDistanceMode(value)) {
    return;
  }
  handleDistanceEntityModeClick(entityIndex, value);
}

/**
 * 设置角度值。
 *
 * @param value 角度，单位为度。
 */
function handleAngleDegreesChange(value: number | null): void {
  if (value === null || !Number.isFinite(value)) {
    return;
  }
  getAssemblyMateController()?.setAngle?.(value * Math.PI / 180);
  syncFromController();
}

/**
 * 设置尺寸反向。
 *
 * @param value 是否反向。
 */
function handleDimensionFlippedChange(value: boolean): void {
  getAssemblyMateController()?.setDimensionFlipped?.(value);
  syncFromController();
}

/**
 * 设置同轴心配合锁定旋转。
 *
 * @param value 是否锁定。
 */
function handleLockRotationChange(value: boolean): void {
  getAssemblyMateController()?.setLockRotation?.(value);
  syncFromController();
}

/**
 * 设置是否显示预览。
 *
 * @param value 是否显示预览。
 */
function handleShowPreviewChange(value: boolean): void {
  getAssemblyMateController()?.setShowPreview?.(value);
  syncFromController();
}

/**
 * 设置是否使第一个选择所属零部件透明。
 *
 * @param value 是否启用第一个选择透明。
 */
function handleFirstSelectionTransparentChange(value: boolean): void {
  getAssemblyMateController()?.setFirstSelectionTransparent?.(value);
  syncFromController();
}
</script>
